import { Org, User } from 'generated/mos/user';

import uuidv4 from 'uuid/v4';


interface RefreshClaims {
  readonly exp: number; // Expiry time in seconds
}

interface AccessClaims {
  readonly exp: number; // Expiry time in seconds
  readonly id: string; // User UUID
  readonly name: string;
  readonly email: string;
  readonly org: string;
}

// AuthSession represents a logged-in user session. It is destroyed on logout.
export interface AuthSession {
  readonly access: string;
  readonly refresh: string;

  readonly accessClaims: AccessClaims;
  readonly refreshClaims: RefreshClaims;
  readonly user: AuthUser;
}

export type AuthTokens = Pick<AuthSession, 'access' | 'refresh'>

export const createAuthSession = ({ access, refresh }: AuthTokens): AuthSession => {
  // TODO: validate claims more thoroughly:
  const accessClaims = parseClaims(access) as AccessClaims;
  const refreshClaims = parseClaims(refresh) as RefreshClaims;

  return {
    access,
    refresh,
    accessClaims,
    refreshClaims,
    user: authUserFromClaims(accessClaims),
  };
};

// XXX(bw): This started out as a class-based auth client, created before I discovered
// just how allergic to classes Redux is. Ideally we'd massage this out somehow, but this
// is a passable stopgap.
export const authSessionSelector = new class {
  public accessExpiry(session: AuthSession): Date {
    return new Date(bufferExpirySeconds(session.accessClaims.exp) * 1000);
  }

  public refreshExpiry(session: AuthSession) {
    return new Date(bufferExpirySeconds(session.refreshClaims.exp) * 1000);
  }

  public refreshExpired(session: AuthSession, at?: Date): boolean {
    return (at || new Date()).getTime() > this.refreshExpiry(session).getTime();
  }

  public accessExpired(session: AuthSession, at?: Date): boolean {
    return (at || new Date()).getTime() > this.accessExpiry(session).getTime() - 2000;
  }

  public authorization(session: AuthSession): string {
    return `Bearer ${session.access}`;
  }
}();

// AuthUser contains the user-related claims from the Access JWT.
export interface AuthUser {
  readonly name: string;
  readonly email: string;
  readonly org: Org.Ref;
  readonly ref: User.Ref;
}

export const authUserFromClaims = (claims: AccessClaims): AuthUser => {
  if (!claims.id || !claims.name || !claims.email || !claims.org) {
    // FIXME: enrich this
    throw new Error(`invalid claims`);
  }
  return {
    name: claims.name,
    email: claims.email,
    org: { typename: 'mos.user.Org' , id: claims.org },
    ref: { typename: 'mos.user.User' as const, id: claims.id },
  };
};

// AuthDevice represents the browser being used. It should outlive AuthSession.
export type AuthDevice = {
  readonly id: string;
  readonly displayName: string;
};

export const createAuthDevice = (args: Omit<AuthDevice, 'id'>): AuthDevice => ({
  id: uuidv4(),
  ...args,
});

function parseClaims(token: string): object {
  // Convert base 64 url to base 64
  const base64 = token
    .replace('-', '+')
    .replace('_', '/')
    .split('.');

  if (base64.length < 2) {
    throw new Error(`could not parse token: ${base64}`);
  }
  return JSON.parse(atob(base64[1]));
}

function bufferExpirySeconds(expirySecs: number): number {
  // Ensure we renew before the token actually expires, rather than as it expires.
  expirySecs -= 20;
  return expirySecs > 0 ? expirySecs : 0;
}
